<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link"
                        class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom
                        title="Agregar Recordatorio"
                        aria-label="Agregar Recordatorio"
                        @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 240px;">
              <label class="filter-label">Estado</label>
              <b-form-select v-model="filter.status" size="sm" :options="arr.select.status" @change="load()"></b-form-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="load()" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row class="reminders-form">
          <b-col lg="7" class="mb-3">
            <FullCalendar ref="remindersCalendar"
                          defaultView="dayGridMonth"
                          height="auto"
                          :plugins="calendarPlugins"
                          :locale="calendarOptions.locale"
                          :weekends="true"
                          :events="calendarEvents"
                          @eventClick="onCalendarEventClick"
                          @dateClick="onCalendarDateClick"
                          :dayRender="onCalendarDayRender"
                          :titleFormat="calendarTitleFormat"/>
            <div class="reminders-calendar-legend small text-muted mt-2">
              <b-badge class="reminders-legend-pending mr-1">&nbsp;</b-badge> Pendiente
              <b-badge class="reminders-legend-notified ml-2 mr-1">&nbsp;</b-badge> Avisado
              <b-badge class="reminders-legend-overdue ml-2 mr-1">&nbsp;</b-badge> Vencido
              <b-badge class="reminders-legend-completed ml-2 mr-1">&nbsp;</b-badge> Completado
              <b-badge class="reminders-legend-permanent ml-2 mr-1">&nbsp;</b-badge> Permanente
            </div>
          </b-col>

          <b-col lg="5">
            <div class="reminders-status-legend">
              <span class="reminders-status-legend__item">
                <span class="reminders-status-dot reminders-status-dot--pending"></span>
                Pendiente
              </span>
              <span class="reminders-status-legend__item">
                <span class="reminders-status-dot reminders-status-dot--notified"></span>
                Avisado
              </span>
              <span class="reminders-status-legend__item">
                <span class="reminders-status-dot reminders-status-dot--overdue"></span>
                Vencido
              </span>
              <span class="reminders-status-legend__item">
                <span class="reminders-status-dot reminders-status-dot--completed"></span>
                Completado
              </span>
              <span class="reminders-status-legend__item">
                <span class="reminders-status-dot reminders-status-dot--permanent"></span>
                Permanente
              </span>
            </div>

            <div v-if="selectedCalendarDate" class="d-flex align-items-center justify-content-between mb-2">
              <span class="small text-muted">Mostrando recordatorios del {{ selectedCalendarDateLabel }}</span>
              <b-button variant="link" size="sm" class="list-view-ghost-btn" aria-label="Quitar filtro por fecha" @click="clearCalendarDateFilter">
                <AppIcon name="x" class="mr-1" />Quitar filtro
              </b-button>
            </div>

            <b-table class="app-table crud-reminders-table"
                  responsive
                  :stacked="isMobileViewport ? 'md' : false"
                  sticky-header="60vh"
                  :hover="true"
                  :small="true"
                  :items="filteredTableItems"
                  :fields="table.fields"
                  :current-page="table.currentPage"
                  :per-page="table.perPage"
                  :tbody-tr-class="rowClass"
                  @row-clicked="onRowClicked"
                  v-if="filteredTableItems.length">

              <template v-slot:table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width }"
                >
              </template>

              <template v-slot:cell(remind_at)="data">
                <div class="reminders-date-stack">
                  <div class="reminders-date-stack__line reminders-date-stack__date">
                    <AppIcon name="calendar_month" class="mr-1" />
                    {{ formatDateOnly(data.item.remind_at) }}
                  </div>
                  <div class="reminders-date-stack__line">
                    <AppIcon name="schedule" class="mr-1" />
                    {{ formatTimeOnly(data.item.remind_at) }}
                  </div>
                  <div class="reminders-date-stack__line reminders-date-stack__relative">
                    <AppIcon name="timelapse" class="mr-1" />
                    {{ fromNow(data.item.remind_at) }}
                  </div>
                </div>
              </template>

              <template v-slot:cell(title)="data">
                <div class="reminders-cell-title-line">
                  <strong class="reminders-cell-title" :title="data.item.title">{{ data.item.title }}</strong>
                  <AppIcon v-if="data.item.task_id"
                           name="assignment"
                           class="reminders-task-table-icon ml-1"
                           v-b-tooltip.hover
                           title="Vinculado a una tarea: su modo lo define la tarea" />
                  <AppIcon name="link"
                           class="reminders-origin-link ml-1"
                           v-if="data.item.origin_url"
                           label="Ir al origen"
                           v-b-tooltip.hover
                           :title="'Ir al origen: ' + (data.item.origin_label || data.item.origin_url)"
                           @click.stop="openOrigin(data.item)" />
                </div>
                <div class="small text-muted reminders-cell-description" v-if="data.item.description" :title="data.item.description">{{ data.item.description }}</div>
                <div class="reminders-recipient-line">
                  <span class="reminders-recipient-label">Para</span>
                  <span class="reminders-recipient-chip" v-for="recipient in reminderRecipients(data.item)" :key="recipient.type + '-' + recipient.id">
                    <AppIcon :name="recipient.icon" class="mr-1" />
                    {{ recipient.label }}
                  </span>
                </div>
              </template>

              <template v-slot:cell(status)="data">
                <b-badge class="reminders-status-badge"
                      :class="statusBadgeClass(data.item)"
                      v-b-tooltip.hover
                      :title="statusLabel(data.item)">
                  {{ statusLabel(data.item) }}
                </b-badge>
              </template>

              <template v-slot:cell(actions)="data">
                <b-dropdown size="sm"
                            variant="link"
                            boundary="window"
                            :toggle-class="isMobileViewport ? 'crud-reminders-action-toggle-labeled' : 'app-table-action-toggle'"
                            :class="{ 'pull-right': !isMobileViewport }"
                            right
                            no-caret>
                  <template v-slot:button-content>
                    <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                    <span v-if="isMobileViewport">Opciones</span>
                  </template>

                  <b-dropdown-item v-if="!data.item.completed_at && managesStatus(data.item)" @click="completeItem(data.item)">
                    <AppIcon name="check2-circle" class="text-success" /> Completar
                  </b-dropdown-item>
                  <b-dropdown-item v-if="!data.item.completed_at && !managesStatus(data.item) && isOwner(data.item)" @click="postpone(data.item)">
                    <AppIcon name="schedule" /> Posponer
                  </b-dropdown-item>
                  <b-dropdown-item v-if="data.item.origin_url" @click="openOrigin(data.item)">
                    <AppIcon name="link" /> Ir al origen
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="!data.item.completed_at || data.item.origin_url" />
                  <b-dropdown-item v-if="isOwner(data.item) && !data.item.completed_at" @click="edit(data.item)">
                    <AppIcon name="edit" /> Editar
                  </b-dropdown-item>
                  <b-dropdown-item v-if="isOwner(data.item) && !data.item.completed_at && !data.item.task_id" @click="toggleStatusManagement(data.item)">
                    <AppIcon :name="managesStatus(data.item) ? 'repeat' : 'check_circle'" /> {{ managesStatus(data.item) ? 'Hacer permanente' : 'Permitir completar' }}
                  </b-dropdown-item>
                  <b-dropdown-item v-if="!data.item.task_id" @click="duplicateItem(data.item)">
                    <AppIcon name="content_copy" /> Duplicar
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="isOwner(data.item) && !data.item.completed_at && !data.item.task_id" />
                  <b-dropdown-item v-if="isOwner(data.item) && !data.item.completed_at && !data.item.task_id" @click="remove(data.item)">
                    <AppIcon name="delete" class="text-danger" /> Eliminar
                  </b-dropdown-item>
                </b-dropdown>
              </template>

            </b-table>
            <EmptyState v-else
                        icon="notifications"
                        :title="selectedCalendarDate ? 'No hay recordatorios el ' + selectedCalendarDateLabel : 'No se encontraron recordatorios'"
                        :description="selectedCalendarDate ? 'Elegí otro día o quitá el filtro para ver todos los recordatorios.' : 'Ajustá el filtro de estado o agregá un nuevo recordatorio.'" />

            <div class="app-table-footer">
              <span class="list-view-toolbar__count">{{ filteredTableItems.length }} registros</span>
              <nav v-if="filteredTableItems.length > table.perPage">
                <b-pagination class="pull-right mb-0"
                              size="sm"
                              pills=""
                              :total-rows="filteredTableItems.length"
                              :per-page="table.perPage"
                              v-model="table.currentPage" />
              </nav>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
        </div>
      </b-card>

      <b-sidebar id="sidebar-reminder-detail"
                 v-model="detailDrawer"
                 right
                 shadow
                 backdrop
                 title="Detalle del recordatorio"
                 width="420px"
                 @hidden="hideDetail">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">{{ statusLabel(itemSelected) }}</span>
              <div>{{ itemSelected.title }}</div>
              <div class="list-view-drawer__meta">
                {{ formatDate(itemSelected.remind_at) }} · {{ fromNow(itemSelected.remind_at) }}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm" variant="outline-secondary"
                        :disabled="!canGoPrevReminder"
                        v-b-tooltip.hover title="Recordatorio anterior"
                        @click="goPrevReminder">
                <AppIcon name="chevron-up" label="Recordatorio anterior" />
              </b-button>
              <b-button size="sm" variant="outline-secondary"
                        :disabled="!canGoNextReminder"
                        v-b-tooltip.hover title="Recordatorio siguiente"
                        @click="goNextReminder">
                <AppIcon name="chevron-down" label="Recordatorio siguiente" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Estado</span>
              <span class="list-view-drawer__stat-value">{{ statusLabel(itemSelected) }}</span>
            </div>
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Avisar antes</span>
              <span class="list-view-drawer__stat-value">{{ notifyBeforeLabel(itemSelected) }}</span>
            </div>
          </div>

          <div v-if="!itemSelected.completed_at && managesStatus(itemSelected)" class="reminders-detail__quick-action">
            <b-button block variant="success" @click="completeItem(itemSelected)">
              <AppIcon name="check2-circle" class="mr-1" /> Completar recordatorio
            </b-button>
          </div>
          <div v-if="!itemSelected.completed_at && !managesStatus(itemSelected) && isOwner(itemSelected)" class="reminders-detail__quick-action">
            <b-button block variant="outline-primary" @click="postpone(itemSelected)">
              <AppIcon name="schedule" class="mr-1" /> Posponer recordatorio
            </b-button>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="article" /> Detalle</div>
            <p class="reminders-detail-description" v-if="itemSelected.description">{{ itemSelected.description }}</p>
            <span v-else class="text-muted small">Sin detalle adicional.</span>
          </div>

          <div v-if="itemSelected.task_id" class="reminders-task-link">
            <AppIcon name="account_tree" />
            <span><strong>Relacionado con una tarea</strong><small>Su modo permanente o normal lo define la tarea.</small></span>
            <b-button size="sm" variant="outline-secondary" @click="openOrigin(itemSelected)">Ver tarea</b-button>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="groups" /> Destinatarios</div>
            <div class="reminders-recipient-line">
              <span class="reminders-recipient-chip" v-for="recipient in reminderRecipients(itemSelected)" :key="recipient.type + '-' + recipient.id">
                <AppIcon :name="recipient.icon" class="mr-1" />{{ recipient.label }}
              </span>
            </div>
          </div>

          <div class="list-view-drawer__actions">
            <b-button v-if="itemSelected.origin_url" size="sm" variant="outline-secondary" @click="openOrigin(itemSelected)">
              <AppIcon name="link" class="mr-1" /> Ir al origen
            </b-button>
            <b-button v-if="isOwner(itemSelected) && !itemSelected.completed_at" size="sm" variant="outline-secondary" @click="edit(itemSelected)">
              <AppIcon name="edit" class="mr-1" /> Editar
            </b-button>
            <b-button v-if="isOwner(itemSelected) && !itemSelected.completed_at && !itemSelected.task_id" size="sm" variant="outline-secondary" @click="toggleStatusManagement(itemSelected)">
              <AppIcon :name="managesStatus(itemSelected) ? 'repeat' : 'check_circle'" class="mr-1" /> {{ managesStatus(itemSelected) ? 'Hacer permanente' : 'Permitir completar' }}
            </b-button>
            <b-button v-if="!itemSelected.task_id" size="sm" variant="outline-secondary" @click="duplicateItem(itemSelected)">
              <AppIcon name="content_copy" class="mr-1" /> Duplicar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && isOwner(itemSelected) && !itemSelected.completed_at && !itemSelected.task_id">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-modal v-model="modal.form.active"
               :size="isPostponeForm ? 'sm' : 'lg'"
               :dialog-class="isPostponeForm ? 'reminders-postpone-dialog' : ''"
               centered
               :key="modal.form._id"
               :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="notifications" /></span>
          <h5 class="app-modal-header__title">{{ modal.form.title }}</h5>
        </div>

        <b-row>
          <b-col md="12" v-if="isPostponeForm">
            <b-form-group label="Posponer hasta">
              <div class="reminders-postpone-options" role="group" aria-label="Período para posponer">
                <button v-for="option in arr.select.postponeOptions"
                        :key="option.value"
                        type="button"
                        class="reminders-postpone-option"
                        :class="{ 'reminders-postpone-option--active': crud.form.postpone_option == option.value }"
                        @click="applyPostponeOption(option.value)">
                  {{ option.text }}
                </button>
              </div>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="!isPostponeForm">
            <b-form-group label="Título">
              <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.title"
                            :disabled="!!crud.form.task_id"
                            placeholder="Qué tenés que recordar"
                            required>
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="!isPostponeForm">
            <b-form-group label="Descripción">
              <app-textarea size="sm"
                               v-model="crud.form.description"
                               rows="3"
                               placeholder="Detalle opcional">
              </app-textarea>
            </b-form-group>
          </b-col>
          <b-col :md="isPostponeForm ? 12 : 6">
            <b-form-group label="Fecha / Hora">
              <b-form-input type="datetime-local"
                            size="sm"
                            v-model="crud.form.remind_at"
                            @input="onManualPostponeDate"
                            required>
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="!isPostponeForm">
            <b-form-group label="Avisar">
              <b-form-select v-model="crud.form.notify_before_minutes" size="sm" :options="arr.select.notifyBefore"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="!isPostponeForm">
            <section class="reminders-recipients-panel" :class="{ 'reminders-recipients-panel--open': recipientsOpen }">
              <button type="button" class="reminders-recipients-panel__header" :aria-expanded="String(recipientsOpen)" @click="recipientsOpen = !recipientsOpen">
                <span><AppIcon name="notifications" /> Notificar a más personas <small>Opcional</small></span>
                <AppIcon :name="recipientsOpen ? 'expand_less' : 'expand_more'" />
              </button>
              <b-collapse v-model="recipientsOpen">
                <div class="reminders-recipients-panel__content">
                  <div class="reminders-recipient-picker">
                    <label>Por roles</label>
                    <FindObject type="roles"
                                ref="rolesRecipientFinder"
                                hide-label
                                placeholder="Buscar y agregar roles"
                                :multiple="true"
                                @select-object="addRecipients('roles_notification', [$event], 'rolesRecipientFinder')"
                                @select-objects="addRecipients('roles_notification', $event, 'rolesRecipientFinder')" />
                    <div v-if="crud.form.roles_notification.length" class="reminders-form-recipients">
                      <span v-for="role in crud.form.roles_notification" :key="'role-' + role.code" class="reminders-form-recipient-chip">
                        <AppIcon name="groups" />{{ role.label }}
                        <button type="button" aria-label="Quitar rol" @click="removeRecipient('roles_notification', role.code)"><AppIcon name="x" /></button>
                      </span>
                    </div>
                    <small>Todo el staff que tenga alguno de estos roles va a recibir el aviso.</small>
                  </div>
                  <div class="reminders-recipient-picker">
                    <label>Personas específicas</label>
                    <FindObject type="staff"
                                ref="staffRecipientFinder"
                                hide-label
                                placeholder="Buscar y agregar personas"
                                :multiple="true"
                                @select-object="addRecipients('staff_notification', [$event], 'staffRecipientFinder')"
                                @select-objects="addRecipients('staff_notification', $event, 'staffRecipientFinder')" />
                    <div v-if="crud.form.staff_notification.length" class="reminders-form-recipients">
                      <span v-for="staff in crud.form.staff_notification" :key="'staff-' + staff.code" class="reminders-form-recipient-chip">
                        <AppIcon name="person" />{{ staff.label }}
                        <button type="button" aria-label="Quitar integrante" @click="removeRecipient('staff_notification', staff.code)"><AppIcon name="x" /></button>
                      </span>
                    </div>
                    <small>Si no elegís destinatarios, el aviso te llega solo a vos.</small>
                  </div>
                </div>
              </b-collapse>
            </section>
          </b-col>
          <b-col md="12" v-if="!isPostponeForm && (crud.form.origin_label || crud.form.origin_url)">
            <div class="small text-muted">
              <AppIcon name="link" />
              Origen: {{ crud.form.origin_label || crud.form.origin_url }}
            </div>
          </b-col>
          <b-col md="12" v-if="!isPostponeForm && crud.form.task_id">
            <div class="reminders-task-linked-notice">
              <AppIcon name="account_tree" />
              <span>Este recordatorio está vinculado a una tarea. Su título y detalle se toman de esa tarea.</span>
            </div>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="cancelForm">
            <AppIcon name="x" class="mr-1" />Cancelar
          </b-button>
          <b-button variant="dark" @click="save()">
            <AppIcon name="save" class="mr-1" />Guardar
          </b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Session from '@/handler/session'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import FindObject from '@/components/inc/find/findObject'
  import moment from 'moment'

  import FullCalendar from '@fullcalendar/vue'
  import dayGridPlugin from '@fullcalendar/daygrid'
  import esLocale from '@fullcalendar/core/locales/es'
  import interactionPlugin from '@fullcalendar/interaction'

  export default {
    components: {
      FullCalendar,
      FindObject,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.RECORDATORIOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudReminders',
          elements: {}
        },
        calendarPlugins: [ dayGridPlugin, interactionPlugin ],
        calendarOptions: {
          locale: esLocale,
        },
        table: {
          items: [],
          fields: [],
          currentPage: 1,
          perPage: 10,
        },
        filtersOpen: false,
        isMobileViewport: false,
        selectedCalendarDate: null,
        itemSelected: null,
        detailDrawer: false,
        reminderOrigin: null,
        recipientsOpen: false,
        filter: {
          status: 'pending',
        },
        arr: {
          select: {
            status: [
              { value: 'pending', text: 'PENDIENTES' },
              { value: 'completed', text: 'COMPLETADOS' },
              { value: '', text: 'TODOS' },
            ],
            notifyBefore: [
              { value: 5, text: '5 minutos antes' },
              { value: 10, text: '10 minutos antes' },
              { value: 15, text: '15 minutos antes' },
              { value: 30, text: '30 minutos antes' },
              { value: 60, text: '1 hora antes' },
              { value: 120, text: '2 horas antes' },
              { value: 1440, text: '1 día antes' },
            ],
            postponeOptions: [
              { value: 'custom', text: 'Fecha personalizada' },
              { value: '1d', text: '1 día' },
              { value: '2d', text: '2 días' },
              { value: '3d', text: '3 días' },
              { value: '7d', text: '7 días' },
              { value: '15d', text: '15 días' },
              { value: '30d', text: '30 días' },
              { value: '3m', text: '3 meses' },
              { value: '6m', text: '6 meses' },
              { value: '1y', text: '1 año' },
            ],
            roles: [],
            staff: [],
          }
        },
        modal: {
          form: {
            active: false,
            _id: '',
            title: '',
          }
        },
        crud: {
          form: {
            id: 0,
            title: '',
            description: '',
            remind_at: '',
            notify_before_minutes: 60,
            roles_notification: [],
            staff_notification: [],
            origin_url: '',
            origin_label: '',
            task_id: 0,
            return_to_task: false,
            manage_status: true,
            postpone_option: 'custom',
            postpone_only: false,
          }
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_reminders') == 'true'
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.configureFields()
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
      this.resetDefaultFilter()
      this.load()
      this.loadRecipientsOptions()

      // Acción directa desde la paleta de comandos o desde "Convertir en
      // recordatorio" (módulo Tareas): abrir el alta al entrar, con título
      // precargado si vino uno.
      if (this.$route.query.new == '1') {
        this.add({ title: this.$route.query.title || '' })
        this.captureReminderOrigin()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
    },
    activated() {
      if (this._start) {
        this._start = false
        return
      }

      this.resetDefaultFilter()
      this.load()
      this.loadRecipientsOptions()

      // Misma acción directa que en mounted(): si la pestaña ya estaba
      // montada (keep-alive), "mounted" no vuelve a correr y la paleta de
      // comandos (o "Convertir en recordatorio") no abriría nada sin este
      // chequeo acá también.
      if (this.$route.query.new == '1') {
        this.add({ title: this.$route.query.title || '' })
        this.captureReminderOrigin()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    computed: {
      filteredTableItems() {
        if (!this.selectedCalendarDate) return this.table.items

        return this.table.items.filter(item => {
          return moment(item.remind_at).format('YYYY-MM-DD') == this.selectedCalendarDate
        })
      },
      visibleTableItems() {
        var start = (this.table.currentPage - 1) * this.table.perPage
        return this.filteredTableItems.slice(start, start + this.table.perPage)
      },
      currentReminderIndex() {
        if (!this.itemSelected) return -1
        return this.visibleTableItems.findIndex(item => item.id == this.itemSelected.id)
      },
      canGoPrevReminder() {
        return this.currentReminderIndex > 0
      },
      canGoNextReminder() {
        return this.currentReminderIndex >= 0 && this.currentReminderIndex < this.visibleTableItems.length - 1
      },
      selectedCalendarDateLabel() {
        if (!this.selectedCalendarDate) return ''
        return moment(this.selectedCalendarDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
      },
      calendarEvents() {
        return this.table.items.map(item => {
          var color = this.eventColor(item)
          // FullCalendar no acepta duración cero: con end <= start descarta
          // el end y aplica su duración por defecto de 1 hora. Usamos un
          // segundo, la mínima duración necesaria para que un recordatorio
          // (cargado con precisión de minutos) no cruce al día siguiente.
          var start = moment(item.remind_at).format('YYYY-MM-DDTHH:mm:ss')
          var end = moment(item.remind_at).add(1, 'second').format('YYYY-MM-DDTHH:mm:ss')
          return {
            id: item.id,
            title: item.title,
            start: start,
            end: end,
            color: color,
            textColor: this.eventTextColor(color),
          }
        })
      },
      isPostponeForm() {
        return !!this.crud.form.id && this.crud.form.postpone_only
      }
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_reminders', value ? 'true' : 'false')
      },
    },
    methods: {
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
        this.configureFields()
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      configureFields() {
        this.table.fields = [
          {key: 'remind_at', label: 'Cuándo', class: 'align-middle ' + this.alignClass('text-left'), width: '118px'},
          {key: 'title', label: 'Recordatorio', class: 'align-middle ' + this.alignClass('text-left')},
          {key: 'status', label: 'Estado', class: 'align-middle ' + this.alignClass('text-center'), width: '92px'},
        ]
        // En mobile la fila abre el drawer y las acciones viven allí: evitar
        // sumar un kebab a una card ya apilada.
        if (!this.isMobileViewport) {
          this.table.fields.push({key: 'actions', label: '', class: 'align-middle text-right', width: '34px'})
        }
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var success = !!options.success
        var icon = options.icon || (danger ? 'warning' : (success ? 'check' : 'help'))
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          dialogClass: 'reminders-completion-confirm-dialog',
          okVariant: danger ? 'danger' : (success ? 'success' : 'dark'),
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      resetDefaultFilter() {
        this.filter.status = 'pending'
        this.table.currentPage = 1
        this.selectedCalendarDate = null
      },
      load(keepDetailOpen = false) {
        let loader = this.$loading.show()

        serviceAPI.obtenerReminders(this.filter)
          .then((response) => {
            loader.hide()
            this.table.items = response.data
            if (keepDetailOpen && this.itemSelected) {
              var refreshedItem = this.table.items.find(item => item.id == this.itemSelected.id)
              if (refreshedItem) this.openDetail(refreshedItem)
              else this.hideDetail()
            }
            this.$nextTick(this.refreshCalendarDateSelection)
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
      loadRecipientsOptions() {
        serviceAPI.obtenerRoles().then(response => {
          this.arr.select.roles = response.data.map(role => ({ code: role.id, label: role.name }))
        })
        serviceAPI.obtenerStaff().then(response => {
          this.arr.select.staff = response.data.map(staff => ({ code: staff.id, label: staff.name }))
        })
      },
      addRecipients(field, recipients, finderRef) {
        var selected = (recipients || []).filter(Boolean)
        if (!selected.length) return

        var current = this.crud.form[field] || []
        selected.forEach(item => {
          var recipient = { code: item.code || item.id, label: item.label || item.name }
          if (recipient.code && !current.some(currentItem => currentItem.code == recipient.code)) current.push(recipient)
        })
        this.$set(this.crud.form, field, current)
        this.$nextTick(() => {
          var finder = this.$refs[finderRef]
          if (finder) finder.clearObjectSelected()
        })
      },
      removeRecipient(field, code) {
        this.crud.form[field] = (this.crud.form[field] || []).filter(item => item.code != code)
      },

      getCurrentStaffId() {
        return Session.getSession().auth.user.selector_session
      },
      isOwner(item) {
        return item.staff_id == this.getCurrentStaffId()
      },

      add(prefill = {}) {
        this.recipientsOpen = false
        this.crud.form.id = 0
        this.crud.form.title = prefill.title || this.$route.query.title || ''
        this.crud.form.description = prefill.description || this.$route.query.description || ''
        // Default: fecha de hoy, hora actual + 1 hora. Si viene un prefill
        // (ej. duplicar) con remind_at propio, se respeta ese.
        this.crud.form.remind_at = prefill.remind_at || moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
        this.crud.form.notify_before_minutes = this.normalizeNotifyBefore(prefill.notify_before_minutes)
        this.crud.form.roles_notification = prefill.roles_notification || []
        this.crud.form.staff_notification = prefill.staff_notification || []
        this.crud.form.task_id = Number(prefill.task_id || this.$route.query.task_id || 0)
        this.crud.form.return_to_task = prefill.return_to_task || this.$route.query.return_to_task == '1'
        this.crud.form.manage_status = true
        this.crud.form.postpone_option = 'custom'
        this.crud.form.postpone_only = false
        this.crud.form.origin_url = prefill.origin_url || (this.crud.form.task_id ? '/st/tasks/list/' + this.crud.form.task_id : '')
        this.crud.form.origin_label = prefill.origin_label || (this.crud.form.task_id ? 'Tarea #' + this.crud.form.task_id : '')

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Recordatorio"
        this.modal.form.active = true
      },
      captureReminderOrigin() {
        if (this.$route.query.return_to_task == '1' && this.$route.query.task_id) {
          this.reminderOrigin = { type: 'task', id: Number(this.$route.query.task_id) }
        } else {
          this.reminderOrigin = null
        }
      },
      returnToReminderOrigin() {
        var origin = this.reminderOrigin
        this.reminderOrigin = null
        if (origin && origin.type == 'task' && origin.id) {
          this.$router.push({ name: 'TasksStaffCrud', params: { taskId: origin.id } })
          return true
        }
        return false
      },
      cancelForm() {
        this.modal.form.active = false
        this.returnToReminderOrigin()
      },
      edit(item, postponeOnly = false) {
        if (item.completed_at) {
          this.$awn.alert("No se puede editar un recordatorio completado")
          return
        }

        this.crud.form.id = item.id
        this.crud.form.title = item.title
        this.crud.form.description = item.description || ''
        this.crud.form.remind_at = moment(item.remind_at).format('YYYY-MM-DDTHH:mm')
        this.crud.form.notify_before_minutes = this.normalizeNotifyBefore(item.notify_before_minutes)
        this.crud.form.roles_notification = this.hydrateRecipients(item.roles_notification, this.arr.select.roles)
        this.crud.form.staff_notification = this.hydrateRecipients(item.staff_notification, this.arr.select.staff)
        this.crud.form.origin_url = item.origin_url || ''
        this.crud.form.origin_label = item.origin_label || ''
        this.crud.form.task_id = item.task_id || 0
        this.crud.form.return_to_task = false
        this.crud.form.manage_status = this.managesStatus(item)
        this.crud.form.postpone_option = 'custom'
        this.crud.form.postpone_only = postponeOnly
        this.recipientsOpen = !!(this.crud.form.roles_notification.length || this.crud.form.staff_notification.length)

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = postponeOnly ? 'Posponer recordatorio' : 'Editar Recordatorio'
        this.modal.form.active = true
      },
      postpone(item) {
        this.edit(item, true)
      },
      hydrateRecipients(jsonValue, options) {
        var ids = jsonValue ? JSON.parse(jsonValue) : []
        return ids.map(id => {
          var found = options.find(opt => opt.code == id)
          return found || { code: id, label: '#' + id }
        })
      },
      parseRecipientIds(jsonValue) {
        if (!jsonValue) return []
        if (Array.isArray(jsonValue)) return jsonValue

        try {
          return JSON.parse(jsonValue) || []
        } catch (e) {
          return []
        }
      },
      recipientLabel(id, options) {
        var found = options.find(opt => opt.code == id)
        return found ? found.label : '#' + id
      },
      statusLabel(item) {
        var labels = {
          completed: 'Completado',
          overdue: 'Vencido',
          notified: 'Avisado',
          pending: 'Pendiente',
          permanent: 'Permanente',
        }
        return labels[this.statusKey(item)]
      },
      statusKey(item) {
        if (!this.managesStatus(item)) return 'permanent'
        if (item.completed_at) return 'completed'
        if (this.isOverdue(item)) return 'overdue'
        if (item.notified_at) return 'notified'
        return 'pending'
      },
      statusDotClass(item) {
        return 'reminders-status-dot--' + this.statusKey(item)
      },
      statusBadgeClass(item) {
        return 'reminders-status-badge--' + this.statusKey(item)
      },
      isOverdue(item) {
        return !!item && !item.completed_at && moment(item.remind_at).isBefore(moment())
      },
      managesStatus(item) {
        return !item || item.manage_status !== false
      },
      toggleStatusManagement(item) {
        if (!item || !this.isOwner(item) || item.completed_at) return
        if (item.task_id) {
          this.$awn.alert('El modo de este recordatorio lo define la tarea vinculada')
          return
        }
        var willManageStatus = !this.managesStatus(item)
        var message = willManageStatus
          ? 'El recordatorio volverá a permitir completarse. ¿Continuar?'
          : 'El recordatorio solo se podrá posponer o eliminar; no se podrá completar. ¿Continuar?'
        this.confirmDialog(message, {
          title: willManageStatus ? 'Permitir completar' : 'Recordatorio permanente',
          icon: willManageStatus ? 'check_circle' : 'repeat',
          okTitle: willManageStatus ? 'HABILITAR' : 'HACER PERMANENTE',
        }).then(confirmed => {
          if (!confirmed) return
          serviceAPI.editarReminder({ id: item.id, manage_status: willManageStatus })
            .then(() => { this.$awn.success('Recordatorio actualizado'); this.load(true) })
            .catch(error => this.$awn.alert(Error.showError(error)))
        })
      },
      reminderRecipients(item) {
        var recipients = [{
          id: 'owner',
          type: 'owner',
          icon: 'person_pin',
          label: 'Vos',
        }]
        var roleIds = this.parseRecipientIds(item.roles_notification)
        var staffIds = this.parseRecipientIds(item.staff_notification)

        roleIds.forEach(id => {
          recipients.push({
            id: id,
            type: 'role',
            icon: 'groups',
            label: this.recipientLabel(id, this.arr.select.roles),
          })
        })

        staffIds.forEach(id => {
          recipients.push({
            id: id,
            type: 'staff',
            icon: 'person',
            label: this.recipientLabel(id, this.arr.select.staff),
          })
        })

        if (recipients.length === 1) {
          recipients[0].label = 'Solo vos'
        }

        return recipients
      },
      normalizeNotifyBefore(value) {
        if (value === undefined || value === null || value === '') return 60
        return Math.max(5, Number(value))
      },
      applyPostponeOption(value) {
        this.crud.form.postpone_option = value || 'custom'
        if (!value || value == 'custom') return
        var presets = {
          '1d': [1, 'day'], '2d': [2, 'days'], '3d': [3, 'days'],
          '7d': [7, 'days'], '15d': [15, 'days'], '30d': [30, 'days'],
          '3m': [3, 'months'], '6m': [6, 'months'], '1y': [1, 'year'],
        }
        var preset = presets[value]
        if (!preset) return
        this.crud.form.remind_at = moment().add(preset[0], preset[1]).format('YYYY-MM-DDTHH:mm')
      },
      onManualPostponeDate() {
        if (this.isPostponeForm) this.crud.form.postpone_option = 'custom'
      },
      save() {
        if (!this.crud.form.title.trim()) {
          this.$awn.alert("El título es obligatorio")
          return
        }
        if (!this.crud.form.remind_at) {
          this.$awn.alert("La fecha y hora del recordatorio es obligatoria")
          return
        }

        let loader = this.$loading.show()

        var payload = {
          id: this.crud.form.id,
          title: this.crud.form.title,
          description: this.crud.form.description,
          remind_at: this.crud.form.remind_at,
          notify_before_minutes: this.crud.form.notify_before_minutes,
          roles_notification: this.crud.form.roles_notification.map(item => item.code),
          staff_notification: this.crud.form.staff_notification.map(item => item.code),
          origin_url: this.crud.form.origin_url,
          origin_label: this.crud.form.origin_label,
          task_id: this.crud.form.task_id || '',
        }
        if (this.isPostponeForm) {
          payload = { id: this.crud.form.id, remind_at: this.crud.form.remind_at }
        }

        var result = this.crud.form.id ? serviceAPI.editarReminder(payload) : serviceAPI.agregarReminder(payload)

        result.then(() => {
          loader.hide()
          this.modal.form.active = false
          this.$awn.success("Recordatorio guardado")
          if (this.returnToReminderOrigin()) return
          this.load(true)
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      remove(item) {
        if (item.task_id) {
          this.$awn.alert('El recordatorio vinculado se quita desde la tarea')
          return
        }
        if (item.completed_at) {
          this.$awn.alert("No se puede eliminar un recordatorio completado")
          return
        }

        this.confirmDialog('¿Eliminar el recordatorio "' + item.title + '"?', {
          title: 'Eliminar recordatorio',
          icon: 'delete',
          danger: true,
          okTitle: 'ELIMINAR',
          cancelTitle: 'Cancelar'
        })
        .then(value => {
          if (value) {
            serviceAPI.eliminarReminder(item.id)
              .then(() => {
                if (this.itemSelected && this.itemSelected.id == item.id) this.hideDetail()
                this.$awn.success("Recordatorio eliminado")
                this.load()
              })
              .catch(error => {
                this.$awn.alert(Error.showError(error))
              })
          }
        })
      },
      completeItem(item) {
        if (!item || item.completed_at || !this.managesStatus(item)) return

        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: 'check2-circle' } }),
          'Completar recordatorio',
        ])
        this.$bvModal.msgBoxConfirm('¿Marcar "' + item.title + '" como completado?', {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'COMPLETAR',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerClass: 'app-confirm-header reminders-confirm-header--success',
          hideHeaderClose: false,
          centered: true,
        }).then(confirmed => {
          if (!confirmed) return

          serviceAPI.completarReminder(item.id)
            .then(() => {
              this.$awn.success("Recordatorio completado")
              this.load(true)
            })
            .catch(error => {
              var message = Error.showError(error)
              this.$awn[message.indexOf('checklist') !== -1 ? 'warning' : 'alert'](message)
            })
        })
      },
      // Sin recurrencia automática: duplicar es la forma simple de "repetir"
      // cuando hace falta. Crea uno nuevo, pendiente, con los mismos datos
      // (menos la fecha, que toma el default de "hoy + 1 hora").
      duplicateItem(item) {
        if (item.task_id) {
          this.$awn.alert('No se puede duplicar un recordatorio vinculado a una tarea')
          return
        }
        this.add({
          title: item.title,
          description: item.description,
          notify_before_minutes: item.notify_before_minutes,
          roles_notification: this.hydrateRecipients(item.roles_notification, this.arr.select.roles),
          staff_notification: this.hydrateRecipients(item.staff_notification, this.arr.select.staff),
          origin_url: item.origin_url,
          origin_label: item.origin_label,
          task_id: item.task_id || 0,
        })
      },

      // Click de fila: los controles de la propia fila conservan su acción
      // y no abren el panel de detalle.
      onRowClicked(item, index, event) {
        if (!item) return
        if (event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if (ignoreTags.includes(event.target.tagName)) return
          if (event.target.closest('.dropdown')) return
        }
        this.openDetail(item)
      },
      openDetail(item) {
        this.itemSelected = item
        this.detailDrawer = true
      },
      hideDetail() {
        this.itemSelected = null
        this.detailDrawer = false
      },
      goPrevReminder() {
        if (!this.canGoPrevReminder) return
        var item = this.visibleTableItems[this.currentReminderIndex - 1]
        if (item) this.openDetail(item)
      },
      goNextReminder() {
        if (!this.canGoNextReminder) return
        var item = this.visibleTableItems[this.currentReminderIndex + 1]
        if (item) this.openDetail(item)
      },
      notifyBeforeLabel(item) {
        var minutes = this.normalizeNotifyBefore(item.notify_before_minutes)
        var option = this.arr.select.notifyBefore.find(option => option.value == minutes)
        return option ? option.text : minutes + ' minutos antes'
      },

      onCalendarEventClick(info) {
        var item = this.table.items.find(i => i.id == info.event.id)
        if (!item) return

        // El evento pasa a ser el contexto de la tabla: al filtrar su día
        // el registro queda visible y resaltado detrás del drawer.
        this.selectedCalendarDate = moment(item.remind_at).format('YYYY-MM-DD')
        this.table.currentPage = 1
        this.$nextTick(() => {
          this.refreshCalendarDateSelection()
          this.openDetail(item)
        })
      },
      onCalendarDateClick(info) {
        var selectedDate = info.dateStr

        if (this.selectedCalendarDate == selectedDate) {
          this.clearCalendarDateFilter()
          return
        }

        this.selectedCalendarDate = selectedDate
        this.table.currentPage = 1
        this.$nextTick(this.refreshCalendarDateSelection)
      },
      clearCalendarDateFilter() {
        this.selectedCalendarDate = null
        this.table.currentPage = 1
        this.$nextTick(this.refreshCalendarDateSelection)
      },
      onCalendarDayRender(info) {
        this.applyCalendarDateSelection(info.el)
      },
      refreshCalendarDateSelection() {
        var calendar = this.$refs.remindersCalendar
        if (!calendar || !calendar.$el) return

        calendar.$el.querySelectorAll('.fc-day[data-date]').forEach(el => {
          this.applyCalendarDateSelection(el)
        })
      },
      applyCalendarDateSelection(el) {
        if (!el) return
        el.classList.toggle(
          'reminders-calendar-day-selected',
          !!this.selectedCalendarDate && el.getAttribute('data-date') == this.selectedCalendarDate
        )
      },
      openOrigin(item) {
        this.$router.push(item.origin_url)
      },

      eventColor(item) {
        if (!this.managesStatus(item)) return '#7357b5'
        if (item.completed_at) return 'var(--app-color-success)'
        if (this.isOverdue(item)) return 'var(--app-color-danger)'
        if (item.notified_at) return 'var(--app-color-warning)'
        return 'var(--app-color-primary)'
      },
      // FullCalendar solo colorea fondo/borde con "color"; el texto queda
      // fijo por CSS y no siempre contrasta bien. Lo forzamos según el fondo:
      // oscuro (pendiente/completado) -> texto blanco, ámbar (avisado) -> texto oscuro.
      eventTextColor(bgColor) {
        return bgColor == 'var(--app-color-warning)' ? 'var(--app-color-text)' : 'var(--app-color-surface)'
      },
      // El titleFormat automático del locale es.js arma "enero de 2026" (el
      // "de" es gramática española correcta para esa combinación, no un
      // error del locale) -- queremos solo "Enero 2026", así que se arma a
      // mano en vez de dejarle el formateo por defecto a FullCalendar.
      //
      // moment.utc(), NO moment(): arg.date.marker es un Date que
      // FullCalendar codifica en el dominio UTC para representar la fecha
      // de pared del mes mostrado (medianoche UTC = 1° del mes), sin
      // importar el timezone real del navegador. Leerlo con moment() local
      // lo reinterpreta en el huso horario del usuario: en Argentina
      // (UTC-3) la medianoche UTC del 1° de agosto cae en 31 de julio
      // 21:00 local, y el título muestra "Julio" en vez de "Agosto".
      calendarTitleFormat(arg) {
        var text = moment.utc(arg.date.marker).format('MMMM YYYY')
        return text.charAt(0).toUpperCase() + text.slice(1)
      },
      rowClass(item, type) {
        if (type !== 'row' || !item) return ''
        return this.detailDrawer && this.itemSelected && item.id == this.itemSelected.id
          ? 'app-table-row-active'
          : ''
      },
      formatDate(date) {
        return moment(date).format('DD/MM/YYYY HH:mm')
      },
      formatDateOnly(date) {
        return moment(date).format('DD/MM/YYYY')
      },
      formatTimeOnly(date) {
        return moment(date).format('HH:mm')
      },
      fromNow(date) {
        return moment(date).fromNow()
      },
    }
  }
</script>
<style>
  .btn.crud-reminders-action-toggle-labeled {
    align-items: center;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    display: inline-flex;
    font-size: var(--app-text-sm);
    height: auto !important;
    justify-content: flex-start;
    min-height: 0;
    padding: 0 !important;
    width: auto !important;
  }
  .btn.crud-reminders-action-toggle-labeled:hover,
  .btn.crud-reminders-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  .reminders-detail__quick-action {
    margin-top: 12px;
  }
  .reminders-detail__quick-action .btn {
    min-height: 38px;
    font-weight: 700;
  }
  .reminders-confirm-header--success {
    border-bottom: 2px solid var(--app-color-success) !important;
    background: var(--app-color-success-soft) !important;
  }
  .reminders-confirm-header--success .app-confirm-header__title {
    color: var(--app-color-success) !important;
  }
  .reminders-completion-confirm-dialog {
    max-width: 370px;
  }
  .modal-dialog.reminders-postpone-dialog {
    max-width: 430px;
    width: calc(100% - 32px);
    margin-left: auto;
    margin-right: auto;
  }
  .reminders-postpone-options {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .reminders-postpone-option {
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 999px;
    color: var(--app-color-text-secondary);
    cursor: pointer;
    font-size: var(--app-text-xs);
    font-weight: 600;
    line-height: 1.2;
    padding: 6px 9px;
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .reminders-postpone-option:hover {
    background: var(--app-color-primary-soft);
    border-color: var(--app-color-primary-200);
    color: var(--app-color-primary-hover);
  }
  .reminders-postpone-option--active,
  .reminders-postpone-option--active:hover {
    background: var(--app-color-primary);
    border-color: var(--app-color-primary);
    color: var(--app-color-surface);
  }
  .reminders-origin-link {
    cursor: pointer;
    color: var(--app-color-primary);
  }
  .reminders-task-linked-notice,
  .reminders-task-link {
    align-items: center;
    background: var(--app-color-primary-soft);
    border: 1px solid var(--app-color-primary-border);
    border-radius: 4px;
    color: var(--app-color-text);
    display: flex;
    font-size: var(--app-text-sm);
    gap: 8px;
    padding: 9px 10px;
  }
  .reminders-task-linked-notice > .material-symbols-rounded,
  .reminders-task-link > .material-symbols-rounded {
    color: var(--app-color-primary);
  }
  .reminders-task-link span {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
  }
  .reminders-task-link small {
    color: var(--app-color-text-secondary);
  }
  .reminders-date-stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 108px;
    padding-right: 8px;
  }
  .reminders-date-stack__line {
    align-items: center;
    color: var(--app-color-text-secondary);
    display: inline-flex;
    font-size: var(--app-text-xs);
    line-height: 1.15;
    white-space: nowrap;
  }
  .reminders-date-stack__date {
    color: var(--app-color-text);
    font-weight: 700;
  }
  .reminders-date-stack__relative {
    color: var(--app-color-text-muted);
  }
  .reminders-cell-title-line {
    align-items: center;
    display: flex;
    max-width: min(48vw, 460px);
    min-width: 0;
  }
  .reminders-cell-title,
  .reminders-cell-description {
    display: block;
    max-width: min(48vw, 460px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .reminders-cell-title {
    min-width: 0;
  }
  .reminders-origin-link {
    flex: 0 0 auto;
  }
  .reminders-task-table-icon {
    color: var(--app-color-primary);
    flex: 0 0 auto;
    font-size: 16px;
  }
  .reminders-form {
    padding-top: 4px;
  }
  .reminders-form .form-group {
    margin-bottom: 18px;
  }
  .reminders-form .form-group > label {
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-sm);
    font-weight: 700;
    margin-bottom: 6px;
  }
  .reminders-form .form-text {
    color: var(--app-color-text-muted);
    font-size: var(--app-text-xs);
    margin-top: 5px;
  }
  .reminders-form .form-control,
  .reminders-form .custom-select {
    background: var(--app-color-surface-subtle);
    border-color: var(--app-color-border);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, .45);
    color: var(--app-color-text);
    transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
  }
  .reminders-form .form-control:hover,
  .reminders-form .custom-select:hover {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-surface);
  }
  .reminders-form .form-control:focus,
  .reminders-form .custom-select:focus {
    background: var(--app-color-surface);
    border-color: var(--app-color-primary);
    box-shadow: 0 0 0 3px var(--app-color-primary-soft);
  }
  .reminders-form .form-control::placeholder {
    color: var(--app-color-text-muted);
    opacity: 1;
  }
  .reminders-form textarea.form-control {
    line-height: 1.5;
    min-height: 86px;
    resize: vertical;
  }
  .reminders-form-recipients {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .reminders-form-recipient-chip {
    align-items: center;
    background: var(--app-color-primary-soft);
    border: 1px solid var(--app-color-info-border);
    border-radius: 999px;
    color: var(--app-color-primary-hover);
    display: inline-flex;
    font-size: var(--app-text-xs);
    font-weight: 600;
    gap: 4px;
    padding: 3px 5px 3px 8px;
  }
  .reminders-form-recipient-chip button {
    align-items: center;
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    padding: 0;
  }
  .reminders-recipients-panel {
    border: 1px solid var(--app-color-border);
    border-radius: var(--app-radius-md);
    overflow: hidden;
  }
  .reminders-recipients-panel--open {
    border-color: var(--app-color-info-border);
  }
  .reminders-recipients-panel__header {
    align-items: center;
    background: var(--app-color-surface-subtle);
    border: 0;
    color: var(--app-color-text);
    cursor: pointer;
    display: flex;
    font-size: var(--app-text-sm);
    font-weight: 700;
    justify-content: space-between;
    padding: 10px 12px;
    text-align: left;
    width: 100%;
  }
  .reminders-recipients-panel__header span,
  .reminders-recipients-panel__header small {
    align-items: center;
    display: inline-flex;
  }
  .reminders-recipients-panel__header small {
    color: var(--app-color-text-muted);
    font-size: var(--app-text-xs);
    font-weight: 500;
    margin-left: 6px;
  }
  .reminders-recipients-panel__content {
    display: grid;
    gap: 14px;
    padding: 12px;
  }
  .reminders-recipient-picker > label {
    color: var(--app-color-text-secondary);
    display: block;
    font-size: var(--app-text-xs);
    font-weight: 700;
    margin-bottom: 5px;
    text-transform: uppercase;
  }
  .reminders-recipient-picker > small {
    color: var(--app-color-text-muted);
    display: block;
    font-size: var(--app-text-xs);
    margin-top: 6px;
  }
  .reminders-detail-description {
    color: var(--app-color-text-secondary);
    line-height: 1.55;
    margin: 0;
    overflow-wrap: anywhere;
    white-space: pre-line;
  }
  .reminders-status-legend {
    align-items: center;
    color: var(--app-color-text-muted);
    display: flex;
    flex-wrap: wrap;
    font-size: var(--app-text-xs);
    gap: 10px;
    justify-content: flex-end;
    margin-bottom: 6px;
  }
  .reminders-status-legend__item {
    align-items: center;
    display: inline-flex;
    gap: 4px;
  }
  .reminders-status-dot {
    border-radius: 999px;
    box-shadow: 0 0 0 2px var(--app-color-surface);
    display: inline-block;
    height: 10px;
    width: 10px;
  }
  .reminders-status-dot--pending {
    background-color: var(--app-color-primary);
  }
  .reminders-status-dot--notified {
    background-color: var(--app-color-warning);
  }
  .reminders-status-dot--overdue {
    background-color: var(--app-color-danger);
  }
  .reminders-status-dot--completed {
    background-color: var(--app-color-success);
  }
  .reminders-status-dot--permanent {
    background-color: #7357b5;
  }
  .reminders-status-badge {
    border: 1px solid transparent;
    border-radius: 999px;
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.2;
    padding: 3px 6px;
    white-space: nowrap;
  }
  .reminders-status-badge--pending {
    background-color: var(--app-color-primary-soft);
    border-color: var(--app-color-info-border);
    color: var(--app-color-primary-hover);
  }
  .reminders-status-badge--notified {
    background-color: var(--app-color-warning-soft);
    border-color: var(--app-color-warning-border);
    color: var(--app-color-warning-hover);
  }
  .reminders-status-badge--overdue {
    background-color: var(--app-color-danger-soft);
    border-color: var(--app-color-danger-border);
    color: var(--app-color-danger-hover);
  }
  .reminders-status-badge--completed {
    background-color: var(--app-color-success-border);
    border-color: var(--app-color-success);
    color: var(--app-color-success-hover);
  }
  .reminders-status-badge--permanent {
    background-color: #f0ebfb;
    border-color: #c9bce9;
    color: #5c438f;
  }
  .reminders-recipient-line {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
  }
  .reminders-recipient-label {
    color: var(--app-color-text-muted);
    font-size: var(--app-text-xs);
    font-weight: 600;
    text-transform: uppercase;
  }
  .reminders-recipient-chip {
    align-items: center;
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 999px;
    color: var(--app-color-text-secondary);
    display: inline-flex;
    font-size: var(--app-text-xs);
    line-height: 1.2;
    padding: 3px 7px;
  }
  .reminders-legend-pending,
  .reminders-legend-notified,
  .reminders-legend-overdue,
  .reminders-legend-completed,
  .reminders-legend-permanent {
    border-radius: 999px;
    display: inline-block;
    height: 9px;
    min-width: 9px;
    padding: 0;
    vertical-align: middle;
    width: 9px;
  }
  .reminders-legend-pending {
    background-color: var(--app-color-primary);
  }
  .reminders-legend-notified {
    background-color: var(--app-color-warning);
  }
  .reminders-legend-overdue {
    background-color: var(--app-color-danger);
  }
  .reminders-legend-completed {
    background-color: var(--app-color-success);
  }
  .reminders-legend-permanent {
    background-color: #7357b5;
  }
  .fc-event {
    cursor: pointer;
  }
  .fc-day.reminders-calendar-day-selected {
    background-color: var(--app-color-primary-soft) !important;
    box-shadow: inset 0 0 0 2px var(--app-color-primary);
  }
  @media (max-width: 767.98px) {
    /* En mobile las columnas se apilan: conservar la leyenda que encabeza
       el listado y ocultar la equivalente que acompaña al calendario. */
    .reminders-calendar-legend {
      display: none;
    }
    .reminders-cell-title-line,
    .reminders-cell-title,
    .reminders-cell-description {
      max-width: 100%;
    }
    .crud-reminders-table.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
  }
</style>
